import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'

/** #9 Service analytics: repair cost by bike, frequently-failing parts, mechanic output. */

interface ByBike { bikeId: string; bikeCode: string; model: string | null; repairs: number; partsCost: number; laborCost: number; totalCost: number }
interface ByPart { partId: string; name: string; category: string | null; unit: string; writeoffs: number; quantity: number; cost: number; bikesCount: number }
interface ByMechanic { mechanicId: string; name: string; orders: number; hours: number; partsCost: number; laborCost: number; totalCost: number }
interface Analytics { byBike: ByBike[]; byPart: ByPart[]; byMechanic: ByMechanic[] }

type Dir = 'asc' | 'desc'

/** Sortable table: click a header to sort, click again to flip direction. */
function SortableTable<T extends object>({
  rows, columns, initialSort,
}: {
  rows: T[]
  columns: { key: keyof T & string; label: string; numeric?: boolean; render?: (r: T) => React.ReactNode }[]
  initialSort: keyof T & string
}) {
  const [sortKey, setSortKey] = useState<string>(initialSort)
  const [dir, setDir] = useState<Dir>('desc')
  const cell = (row: T, key: string): unknown => (row as Record<string, unknown>)[key]

  const sorted = useMemo(() => {
    const copy = [...rows]
    copy.sort((a, b) => {
      const av = cell(a, sortKey); const bv = cell(b, sortKey)
      if (typeof av === 'number' && typeof bv === 'number') return dir === 'asc' ? av - bv : bv - av
      return dir === 'asc'
        ? String(av ?? '').localeCompare(String(bv ?? ''), 'ru')
        : String(bv ?? '').localeCompare(String(av ?? ''), 'ru')
    })
    return copy
  }, [rows, sortKey, dir])

  const toggle = (k: string) => {
    if (k === sortKey) setDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortKey(k); setDir('desc') }
  }

  if (rows.length === 0) return <p className="text-sm text-gray-400 py-3">Нет данных за период.</p>

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-400 uppercase">
            {columns.map((c) => (
              <th
                key={c.key}
                onClick={() => toggle(c.key)}
                className={`py-2 cursor-pointer select-none whitespace-nowrap ${c.numeric ? 'text-right' : ''}`}
              >
                {c.label}{sortKey === c.key ? (dir === 'asc' ? ' ↑' : ' ↓') : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sorted.map((r, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c.key} className={`py-1.5 ${c.numeric ? 'text-right tabular-nums' : ''}`}>
                  {c.render ? c.render(r) : String(cell(r, c.key) ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function ServiceReportPage() {
  const today = new Date()
  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
  const [from, setFrom] = useState(monthAgo.toISOString().slice(0, 10))
  const [to, setTo] = useState(today.toISOString().slice(0, 10))

  const { data, isLoading } = useQuery<Analytics>({
    queryKey: ['service-analytics', from, to],
    queryFn: () => api.get('/workorders/analytics', { params: { from, to: `${to}T23:59:59` } }).then((r) => r.data),
  })

  const money = (n: number) => `${n.toLocaleString('ru-RU')} ₽`

  return (
    <div className="p-4 space-y-6 max-w-4xl mx-auto">
      <h2 className="text-lg font-bold">🔧 Отчёты по сервису</h2>

      <div className="flex gap-2 items-end flex-wrap">
        <div>
          <label className="block text-xs text-gray-500 mb-1">С</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="input" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">По</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="input" />
        </div>
      </div>

      {isLoading && <p className="text-gray-500 text-sm">Загрузка…</p>}

      {data && (
        <>
          <section className="bg-white rounded-2xl p-4">
            <h3 className="font-semibold mb-1">Затраты на ремонт по велосипедам</h3>
            <p className="text-xs text-gray-400 mb-2">Кто дороже всех обходится и кто чаще ломается.</p>
            <SortableTable<ByBike>
              rows={data.byBike}
              initialSort="totalCost"
              columns={[
                { key: 'bikeCode', label: 'Велосипед', render: (r) => `#${r.bikeCode}${r.model ? ` · ${r.model}` : ''}` },
                { key: 'repairs', label: 'Ремонтов', numeric: true },
                { key: 'partsCost', label: 'Запчасти', numeric: true, render: (r) => money(r.partsCost) },
                { key: 'laborCost', label: 'Работа', numeric: true, render: (r) => money(r.laborCost) },
                { key: 'totalCost', label: 'Итого', numeric: true, render: (r) => money(r.totalCost) },
              ]}
            />
          </section>

          <section className="bg-white rounded-2xl p-4">
            <h3 className="font-semibold mb-1">Часто меняемые запчасти</h3>
            <p className="text-xs text-gray-400 mb-2">Что расходуется чаще всего и на какую сумму.</p>
            <SortableTable<ByPart>
              rows={data.byPart}
              initialSort="writeoffs"
              columns={[
                { key: 'name', label: 'Запчасть' },
                { key: 'category', label: 'Категория', render: (r) => r.category ?? '—' },
                { key: 'writeoffs', label: 'Списаний', numeric: true },
                { key: 'quantity', label: 'Кол-во', numeric: true, render: (r) => `${r.quantity} ${r.unit}` },
                { key: 'bikesCount', label: 'Великов', numeric: true },
                { key: 'cost', label: 'Сумма', numeric: true, render: (r) => money(r.cost) },
              ]}
            />
          </section>

          <section className="bg-white rounded-2xl p-4">
            <h3 className="font-semibold mb-1">Работа механиков</h3>
            <p className="text-xs text-gray-400 mb-2">Объём выполненных работ за период. Оценку качества делаете сами по списку ремонтов.</p>
            <SortableTable<ByMechanic>
              rows={data.byMechanic}
              initialSort="orders"
              columns={[
                { key: 'name', label: 'Механик' },
                { key: 'orders', label: 'Нарядов', numeric: true },
                { key: 'hours', label: 'Часов', numeric: true },
                { key: 'partsCost', label: 'Запчасти', numeric: true, render: (r) => money(r.partsCost) },
                { key: 'laborCost', label: 'Работа', numeric: true, render: (r) => money(r.laborCost) },
                { key: 'totalCost', label: 'Итого', numeric: true, render: (r) => money(r.totalCost) },
              ]}
            />
          </section>
        </>
      )}
    </div>
  )
}
